import { Row, Col, Card, Button } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import { useGetProductsQuery } from '../slices/productsApiSlice';
import { useGetStocksQuery } from '../slices/stocksApiSlice';
import { useGetSalesQuery } from '../slices/salesApiSlice';
import { useGetTransfersQuery } from '../slices/transfersApiSlice';
import { useGetSuppliesQuery } from '../slices/suppliesApiSlice';
import Loader from '../components/Loader';
import Message from '../components/Message';

import Meta from '../components/Meta';
import { useDispatch } from 'react-redux';
import { addToCart } from '../slices/cartSlice';
import { useState, useEffect } from 'react';

const StaffHomeScreen = () => {
  const { pageNumber, keyword } = useParams();
  const dispatch = useDispatch();
  const { data, isLoading, error } = useGetProductsQuery({ keyword, pageNumber });

  // Fetch stock-related data
  const { data: stocksData } = useGetStocksQuery();
  const { data: salesData } = useGetSalesQuery();
  const { data: suppliesData } = useGetSuppliesQuery();
  const { data: transfersData } = useGetTransfersQuery();

  // State variables
  const [searchTerm, setSearchTerm] = useState('');
  const [qtys, setQtys] = useState({});
  const [initialStock, setInitialStock] = useState({});
  const [purchases, setPurchases] = useState({});
  const [salesQty, setSalesQty] = useState({});
  const [transfersQty, setTransfersQty] = useState({});
  const [stockBalances, setStockBalances] = useState({});

  // Handle quantity change
  const handleQtyChange = (productId, value) => {
    setQtys({ ...qtys, [productId]: value });
  };

  // Add to cart handler
  const addToCartHandler = (product) => {
    const qty = qtys[product._id] || 0;
    dispatch(addToCart({ ...product, qty }));
  };

  // Search functionality
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Calculate stock-related data
  useEffect(() => {
    if (!stocksData?.stocks || !salesData?.sales || !suppliesData?.supplies || !transfersData?.transfers) return;

    const firstDate = stocksData.stocks
      .map((item) => new Date(item.date))
      .reduce((minDate, currentDate) => (currentDate < minDate ? currentDate : minDate), new Date())
      .toISOString()
      .split('T')[0];

    const secondDate = new Date().toISOString().split('T')[0];

    const calculateMetrics = (product, type) => {
      const dataMap = { sales: salesData?.sales, supplies: suppliesData?.supplies, transfers: transfersData?.transfers };
      const itemKeyMap = { sales: 'saleItems', supplies: 'supplyItems', transfers: 'transferItems' };

      const records = dataMap[type]?.flatMap((item) =>
        item[itemKeyMap[type]]?.filter(
          (subItem) =>
            subItem.name === product.name &&
            new Date(item.date) >= new Date(firstDate) &&
            new Date(item.date) <= new Date(secondDate)
        ) || []
      );

      return records?.reduce((total, record) => total + Number(record.qty || 0), 0) || 0;
    };

    const getInitialStock = (product) =>
      stocksData?.stocks
        .flatMap((item) =>
          item.stockItems?.filter((subItem) => subItem.name === product.name && new Date(item.date) <= new Date(firstDate)) || []
        )
        .reduce((total, subItem) => total + Number(subItem.qty || 0), 0);

    const initialStockTemp = {};
    const purchasesTemp = {};
    const salesQtyTemp = {};
    const transfersQtyTemp = {};
    const stockBalancesTemp = {};

    data?.products?.forEach((product) => {
      initialStockTemp[product._id] = getInitialStock(product);
      purchasesTemp[product._id] = calculateMetrics(product, 'supplies');
      salesQtyTemp[product._id] = calculateMetrics(product, 'sales');
      transfersQtyTemp[product._id] = calculateMetrics(product, 'transfers');
      stockBalancesTemp[product._id] =
        initialStockTemp[product._id] + purchasesTemp[product._id] - salesQtyTemp[product._id] - transfersQtyTemp[product._id];
    });

    setInitialStock(initialStockTemp);
    setPurchases(purchasesTemp);
    setSalesQty(salesQtyTemp);
    setTransfersQty(transfersQtyTemp);
    setStockBalances(stockBalancesTemp);
  }, [stocksData, salesData, suppliesData, transfersData, data]);

  const filteredProducts = data?.products?.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      {keyword && (
        <Link to="/" className="btn btn-light mb-4">
          Go Back
        </Link>
      )}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search products..."
          className="form-control"
          value={searchTerm}
          onChange={handleSearch}
          style={{ maxWidth: '400px', margin: '0 auto' }}
        />
      </div>
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || error.error}</Message>
      ) : (
        <>
          <Meta />
          <h1>Products</h1>
          <Row>
            {filteredProducts?.map((product) => (
              <Col key={product._id} xs={12} sm={6} md={6} lg={3}>
                <Card className="mb-4 shadow-sm">
                  {product.images?.length > 0 && (
                    <Card.Img
                      variant="top"
                      src={product.images[0]}
                      alt={`Product ${product.name}`}
                      style={{ width: '75%', height: '150px', objectFit: 'cover', borderRadius: '10px' }}
                    />
                  )}
                  <Card.Body>
                    <Card.Title>{product.name}</Card.Title>
                    <Card.Text>Opening Stock: {initialStock[product._id] || 'Calculating...'}</Card.Text>
                    <Card.Text>Supply: {purchases[product._id] || 'Calculating...'}</Card.Text>
                    <Card.Text>Sales: {salesQty[product._id] || 'Calculating...'}</Card.Text>
                    <Card.Text>Transfers: {transfersQty[product._id] || 'Calculating...'}</Card.Text>
                    <Card.Text>Stock Balance: {stockBalances[product._id] || 'Calculating...'}</Card.Text>

                    {/* Quantity Selection & Add to Cart */}
                    <div className="d-flex align-items-center mb-2">
                      <Button
                        variant="light"
                        className="btn-sm"
                        onClick={() =>
                          handleQtyChange(product._id, Math.max((qtys[product._id] || 0) - 1, 0))
                        }
                        disabled={(qtys[product._id] || 0) === 0}
                      >
                        -
                      </Button>
                      <span className="mx-2">{qtys[product._id] || 0}</span>
                      <Button
                        variant="light"
                        className="btn-sm"
                        onClick={() =>
                          handleQtyChange(product._id, Math.min((qtys[product._id] || 0) + 1, product.countInStock))
                        }
                        disabled={(qtys[product._id] || 0) === product.countInStock}
                      >
                        +
                      </Button>
                    </div>

                    <Button variant="warning" className="btn-sm" onClick={() => addToCartHandler(product)}>
                      Add To Cart
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}
    </>
  );
};

export default StaffHomeScreen;